import { z } from 'zod'
import { oc } from '@orpc/contract'
import { pageSchema } from '../../utils/page.contract'
import { recordQuerySchema } from '../../utils/record-query.contract'
import { cpgDocumentDetailSchema, cpgDocumentListItemSchema } from './cpg-registry.contract'

/** Filterable/sortable columns of the documents table (translated in the request service). */
export const CPG_DOCUMENT_QUERY_FIELDS = [
  'id',
  'source',
  'externalId',
  'title',
  'version',
  'status',
  'publishedAt',
  'lastSeenAt',
] as const

/**
 * POST because the filter/order input doesn't fit query params (same precedent as
 * POST /v2/patients). `scope: 'review'` = the human work queue, which is exactly the
 * needs_registration documents — activation is the only human transition. `familyId`
 * narrows to docs assigned to a family (the Families view's document list).
 */
export const listCpgDocuments = oc
  .route({ method: 'POST', path: '/v2/cpg/documents/list' })
  .input(
    recordQuerySchema(CPG_DOCUMENT_QUERY_FIELDS).extend({
      scope: z.enum(['review', 'all']).default('all'),
      familyId: z.number().int().positive().optional(),
    }),
  )
  .output(z.object({ page: pageSchema, data: z.array(cpgDocumentListItemSchema) }))

export const getCpgDocument = oc
  .route({ method: 'GET', path: '/v2/cpg/documents/{id}' })
  .input(z.object({ id: z.coerce.number().int().positive() }))
  .output(z.object({ data: cpgDocumentDetailSchema }))

export const CPG_DOCUMENT_FAMILY_ACTION_VALUES = [
  'add',
  'remove',
  'confirm',
  'confirm_all',
] as const
export type CpgDocumentFamilyAction = (typeof CPG_DOCUMENT_FAMILY_ACTION_VALUES)[number]

/**
 * One mutation endpoint for a document's family assignments (mirrors taskRunAction's
 * action-enum style). `add` assigns manually (born confirmed); `confirm` stamps the caller
 * on an AI suggestion; `confirm_all` confirms every unconfirmed suggestion; `remove`
 * deletes the assignment. Every write lands a cpg_registry_audit row.
 */
export const cpgDocumentFamilyAction = oc
  .route({ method: 'POST', path: '/v2/cpg/documents/{id}/families' })
  .input(
    z
      .object({
        id: z.coerce.number().int().positive(),
        action: z.enum(CPG_DOCUMENT_FAMILY_ACTION_VALUES),
        familyId: z.number().int().positive().optional(),
      })
      .refine((v) => v.action === 'confirm_all' || v.familyId !== undefined, {
        message: 'familyId is required for add/remove/confirm',
      }),
  )
  .output(z.object({ data: cpgDocumentDetailSchema }))

/**
 * The registration gate: needs_registration → active, superseding the predecessor in the
 * same transaction. Preconditions: no unconfirmed AI suggestions remain, and at least one
 * confirmed family unless `allowNoFamilies` (the UI's explicit "activate with no families"
 * confirmation). `supersedesDocumentId` overrides the auto-detected predecessor (cross-
 * artifact succession); null = explicitly none; omitted = auto-detect.
 */
export const activateCpgDocument = oc
  .route({ method: 'POST', path: '/v2/cpg/documents/{id}/activate' })
  .input(
    z.object({
      id: z.coerce.number().int().positive(),
      supersedesDocumentId: z.number().int().positive().nullish(),
      allowNoFamilies: z.boolean().default(false),
    }),
  )
  .output(z.object({ data: cpgDocumentDetailSchema }))

// There is no withdraw endpoint: removal is corpus-driven, never a human action. The sync
// withdraws an active document whose topic left the corpus and restores it when identical
// content reappears (docs/db/DB_SCHEMA_CPG.md). A document nobody wants to activate simply
// stays in needs_registration.
